import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseURL, socketId } from "configs/axiosConfig";
import Button from "../../components/Button";
import {
  FaAngleLeft,
  FaAngleRight,
  FaEnvelope,
  FaPhoneAlt,
  FaRegCommentDots,
  FaUser,
} from "react-icons/fa";
import ProcessingBar from "utils/Processingbar";
import { IoIosTimer } from "react-icons/io";
import {
  CiCalendarDate,
  CiTimer,
  CiUser,
  CiMail,
  CiPhone,
  CiShoppingCart,
  CiForkAndKnife,
  CiDeliveryTruck,
  CiBellOn,
  CiCircleChevLeft,
  CiCircleChevRight,
  CiChat1,
  CiMoneyCheck1,
  CiCreditCard1,
  CiGlobe,
} from "react-icons/ci";

import { toast } from "react-toastify";
import ProcessingBarSmall from "utils/ProcessingbarSmall";
import ConfirmationModal from "./components/ConfirmationModal";
import MobileConfirmModal from "./components/MobileConfirmModal";
import { setAuthModalOpen } from "../../redux/features/authModalSlice";
import { createOrderService } from "api/order/OrderApi";
import { emptyCart } from "../../redux/features/cartState";
import { stripeCheckoutAction } from "../../redux/features/orderSlide";

const RenderSidebar = ({
  formData,
  handleNext,
  handleBack,
  currentStep,
  isFirstStep,
  isLastStep,
  stepProcessing,
  isStepValid,
}) => {
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart.cart);
  const user = useSelector((state) => state.auth.user);
  const [operatorInfo, setOperatorInfo] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState(null);

  // console.log({ stepProcessing });

  const selectedDate = new Date(formData.selectedDateTime);

  const localTime = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // <-- use 24-hour format
  }).format(selectedDate);

  const localDay = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    weekday: "short",
    day: "numeric",
    month: "long",
  }).format(selectedDate);

  const orderingIcons = {
    pickup: <CiShoppingCart className="text-sm text-textGray" />,
    delivery: <CiDeliveryTruck className="text-sm text-textGray" />,
    table: <CiForkAndKnife className="text-sm text-textGray" />,
    "table-preorder": <CiBellOn className="text-sm text-textGray" />,
  };

  const paymentIcons = {
    cash: <CiMoneyCheck1 className="text-sm text-textGray" />,
    card: <CiCreditCard1 className="text-sm text-textGray" />,
    online: <CiGlobe className="text-sm text-textGray" />,
  };

  const stepKeyMap = ["dateTime", "ordering", "payment", "contact", "done"];
  const currentStepKey = stepKeyMap[currentStep];
  const isCurrentProcessing = stepProcessing?.[currentStepKey] || false;

  const [isProcessing, setIsProcessing] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMobileConfirmModal, setShowMobileConfirmModal] = useState(false);

  useEffect(() => {
    const fetchOperators = async () => {
      setIsLoading(true);
      const uniqueOperatorIds = [...new Set(cart.map((item) => item.operator))];

      try {
        const operatorData = await Promise.all(
          uniqueOperatorIds.map(async (id) => {
            if (!operatorInfo[id]) {
              const response = await axios.get(
                `${baseURL}/api/products/operatorInfoinAuthor?operator=${id}`
              );
              return { id, data: response.data.operator };
            }
            return null;
          })
        );

        const updatedOperatorInfo = {};
        operatorData.forEach((entry) => {
          if (entry) {
            updatedOperatorInfo[entry.id] = entry.data;
          }
        });

        setOperatorInfo((prev) => ({ ...prev, ...updatedOperatorInfo }));
      } catch (error) {
        console.error("Error fetching operator data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (cart.length > 0) {
      fetchOperators();
    } else {
      setIsLoading(false);
    }
  }, [cart]);

  const cartWithOperator = cart.map((item) => ({
    ...item,
    operatorInfo: operatorInfo[item.operator] || null,
  }));

  const groupedByOperator = cartWithOperator.reduce((acc, item) => {
    const opId = item.operator;
    if (!acc[opId]) acc[opId] = { operatorInfo: item.operatorInfo, items: [] };
    acc[opId].items.push(item);
    return acc;
  }, {});

  const totalAmount = cart.reduce(
    (total, item) => total + item.price * item.count,
    0
  );
  const totalServices = cart.length;

  // send to backend to create New Order

  const dispatch = useDispatch();

  const placeOrderHandler = async () => {
    // console.log({ formData });

    if (!user) {
      dispatch(setAuthModalOpen(true));
      return;
    }
    const listofOperators = [
      ...new Set(cartWithOperator.map((item) => item.operator)),
    ];

    const isOnlinePayment = formData?.paymentMethod === "online";

    // // 🔔 Notify operator via socket

    try {
      const orderData = {
        user: user?._id,
        operator: listofOperators,
        customer_info: {
          email: user?.username,
          name: user?.displayName,
          phone: user?.phone,
          message: formData?.message,
        },
        orderItems: cartWithOperator?.map((item) => ({
          product: item?._id,
          qty: item?.count,
          name: item?.title,
          operator: item?.operator,
          price: item?.price,
          operatorInfo: item?.operatorInfo,
        })),
        payments: {
          paymentMethod: formData?.paymentMethod,
          status: isOnlinePayment ? "pending" : "completed",
          ...(isOnlinePayment ? {} : { paymentDate: new Date() }),
        },
        orderingMethod: formData?.orderingMethod,
        timeSelected: formData?.selectedDateTime,
        totalAmount,
        subTotalPrice: totalAmount,
      };
      // console.log({ orderData });

      setIsProcessing(true); // Start loading

      const res = await createOrderService(orderData);
      // console.log({ res });
      const newOrder = res?.order;
      if (!newOrder) throw new Error("Order not returned from backend");

      setOrder(newOrder); // save to state

      // console.log({ listofOperators });

      listofOperators.forEach((operatorId) => {
        socketId.emit("sendOrderNotification", {
          senderId: user._id,
          receiverId: operatorId,
          orderId: newOrder?.order?._id,
          // orderInfo: orderData,
          orderInfo: {
            customer_info: orderData.customer_info,
            orderItems: orderData.orderItems,
            paymentMethod: orderData.payments.paymentMethod,
            selectedTime: orderData.timeSelected,
            totalAmount: orderData.totalAmount,
            orderingMethod: orderData.orderingMethod,
          },
        });
      });

      setIsProcessing(false);

      dispatch(emptyCart());

      navigate(`/checkout/order/${res?.order?._id}`);
      // Show success message
      toast.success("Order placed successfully!");
      if (!isOnlinePayment) {
        if (res.emailStatus === "success") {
          toast.success("Email sent successfully! Please check your mail");
        } else {
          toast.error("Email sent failed. Contact your administrator");
        }
      }

      return newOrder; // ✅ return the order
    } catch (error) {
      console.error("Order placement failed:", error);
      toast.error(error || "Something went wrong. Please try again.");
      return null; // <-- add this
    } finally {
      setTimeout(() => setIsProcessing(false), 2000); // Hide loader after animation
    }
  };

  // check out throught Stripe
  const handlePayment = (orderToUse) => {
    if (!orderToUse) {
      toast.error("Order data not available for payment.");
      return;
    }

    toast.success(`handlePayment by stripe id ${orderToUse?._id}`);
    console.log({ orderToUse });

    dispatch(
      stripeCheckoutAction({
        id: orderToUse._id,
        orderItems: orderToUse.orderItems,
        email: user?.username,
      })
    )
      .unwrap()
      .then((url) => {
        console.log("Stripe Checkout URL:", url);
        window.location.href = url;
      })
      .catch((error) => {
        console.error("Payment Error:", error);
      });
  };

  const handleConfirm = async () => {
    setIsProcessing(true);

    try {
      if (formData.paymentMethod === "online") {
        let finalOrder = order;

        if (!order) {
          finalOrder = await placeOrderHandler();
          console.log({ finalOrder });
          if (finalOrder?._id) {
            toast.success(
              `New order #${finalOrder._id} created for online payment.`
            );
            setOrder(finalOrder);
          }
        }
        await new Promise((res) => setTimeout(res, 2000));
        await handlePayment(finalOrder); // 👈 Pass the newly created order directly
      } else {
        await new Promise((res) => setTimeout(res, 2000));
        await placeOrderHandler();
      }

      setTimeout(() => {
        setShowConfirmModal(false);
        setShowMobileConfirmModal(false);
      }, 500);
    } catch (error) {
      console.error("Order failed:", error);
      toast.error("Order failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) return <ProcessingBar />;

  return (
    <>
      <div className="relative w-full rounded-2xl p-3 min-h-[760px]">
        {/* Desktop view */}
        <div className="hidden md:block space-y-0 ">
          <div className="max-h-[300px] flex-1 overflow-y-auto space-y-0 scrollbar-thin ">
            {Object.values(groupedByOperator).map(({ operatorInfo, items }) => {
              if (!operatorInfo) return null;

              const subTotal = items.reduce(
                (sum, i) => sum + i.price * i.count,
                0
              );
              return (
                <div
                  key={operatorInfo._id}
                  className="space-y-6 border border-textGray rounded-xl bg-drylight p-3"
                >
                  <div
                    className="flex items-center space-x-4 cursor-pointer "
                    onClick={() =>
                      navigate(`/operator/menu/${operatorInfo._id}`)
                    }
                  >
                    <img
                      src={operatorInfo.photo || "/default-avatar.png"}
                      alt={operatorInfo.name || "Operator"}
                      className="w-16 h-16 rounded-full object-cover border border-neutral-300"
                    />
                    <div>
                      <p className="text-sm font-semibold">
                        {operatorInfo.name}
                      </p>
                      <p className="text-[12px] text-neutral-500 dark:text-textGray">
                        {operatorInfo.address}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item._id} className="flex justify-between">
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-textGray">
                            {item.count} × ${item.price.toFixed(2)}
                          </p>
                        </div>
                        <p className="font-medium">
                          ${(item.count * item.price).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 flex justify-between font-semibold">
                    <span>Subtotal</span>
                    <span>${subTotal.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className=" px-3 pb-3">
            {formData.selectedDateTime && (
              <div className="border-t border-border pt-3 text-sm text-neutral-700 dark:text-neutral-300 space-y-2">
                <p className="font-semibold">Time</p>
                <div className="flex items-center space-x-2">
                  {stepProcessing?.dateTime ? (
                    <ProcessingBar />
                  ) : (
                    <>
                      <CiCalendarDate className="mr-1" />
                      <span className="mr-1">{localDay}</span>
                      &nbsp;&nbsp;-
                      <IoIosTimer className="mx-1" />
                      <span>{localTime}</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="px-3 pb-3">
            {formData.orderingMethod && (
              <div className="border-t  border-border pt-3 text-sm text-neutral-700 dark:text-neutral-300 space-y-2">
                <p className="font-normal">Ordering Method</p>
                <div className="flex items-center space-x-2 text-text capitalize">
                  {orderingIcons[formData.orderingMethod]}
                  <span>
                    {stepProcessing?.ordering ? (
                      <div className="ml-8">
                        <ProcessingBar />
                      </div>
                    ) : (
                      formData.orderingMethod.replace("-", " ")
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="px-3">
            {formData.paymentMethod && (
              <div className="border-t border-border pt-3 text-sm text-neutral-700 dark:text-neutral-300 space-y-2">
                <p className="font-normal">Payment Method</p>
                <div className="flex items-center space-x-2 text-text capitalize">
                  {paymentIcons[formData.paymentMethod]}
                  <span>
                    {stepProcessing?.payment ? (
                      <div className="ml-8">
                        <ProcessingBar />
                      </div>
                    ) : (
                      formData.paymentMethod.replace("-", " ")
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div className="p-3">
            <div className="space-y-2 pt-3 text-sm dark:text-textGray border-t border-border">
              <p className="font-normal text-text">Contact</p>

              {formData.name && (
                <p className="flex items-start space-x-2">
                  <CiUser className="mt-1 text-text" />
                  <span>{formData.name}</span>
                </p>
              )}

              {formData.email && (
                <p className="flex items-start space-x-2">
                  <CiMail className="mt-1 text-text" />
                  <span>{formData.email}</span>
                </p>
              )}

              {formData.mobile && (
                <p className="flex items-start space-x-2">
                  <CiPhone className="mt-1 text-text" />
                  <span>{formData.mobile}</span>
                </p>
              )}

              {formData.message && (
                <p className="flex items-start space-x-2">
                  <CiChat1 className="mt-1 text-text" />
                  <span>{formData.message}</span>
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-border p-3 space-y-3  dark:bg-neutral-900 sticky bottom-0 z-10">
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span>${totalAmount.toFixed(2)}</span>
            </div>

            {/* Navigation buttons (desktop only) */}
            <div className="flex justify-end space-x-2 pt-3">
              {!isFirstStep && (
                <Button
                  inverse
                  onClick={handleBack}
                  className="px-6 py-2 bg-black text-white rounded-full bg-redlight min-w-[120px]"
                >
                  Back
                </Button>
              )}

              <Button
                big
                disabled={isCurrentProcessing || !isStepValid}
                onClick={async () => {
                  if (!isLastStep) {
                    handleNext();
                    return;
                  }

                  if (isLastStep) {
                    setShowConfirmModal(true); // open modal first
                  } else {
                    handleNext();
                  }
                }}
                className="px-6 py-2 bg-black text-white rounded-full bg-redlight min-w-[120px]"
              >
                {isCurrentProcessing ? (
                  <ProcessingBar />
                ) : isLastStep ? (
                  "Confirm"
                ) : (
                  "Next"
                )}
              </Button>

              <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                paymentMethod={formData.paymentMethod}
                isProcessing={isProcessing}
                onConfirm={handleConfirm}
              />
            </div>
          </div>
        </div>

        {/* Mobile bottom fixed bar */}
        <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-dry dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 p-4 flex justify-between items-center md:hidden shadow-md">
          <div>
            <p className="text-lg font-semibold">${totalAmount.toFixed(2)}</p>
            <p className="text-sm text-textGray flex flex-wrap items-center gap-1">
              {totalServices} food{totalServices > 1 ? "s" : ""}
              {stepProcessing?.ordering ? (
                <span className="ml-2">
                  <ProcessingBarSmall />
                </span>
              ) : (
                <>
                  {" "}
                  •<span>{formData?.orderingMethod}</span>
                </>
              )}
              {stepProcessing?.payment ? (
                <span className="ml-2">
                  <ProcessingBarSmall />
                </span>
              ) : (
                <>
                  • <span>{formData?.paymentMethod}</span>
                </>
              )}
            </p>

            {formData.message !== "" && (
              <p className="text-sm text-neutral-500 flex items-center italic">
                {" "}
                <FaRegCommentDots className="mr-1 text-redlight" />
                {formData.message}
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            {!isFirstStep && (
              <Button
                big
                inverse
                onClick={handleBack}
                className={`px-6 py-2 bg-black text-white rounded-full bg-redlight `}
              >
                <FaAngleLeft size={20} />
              </Button>
            )}

            <Button
              big
              disabled={isCurrentProcessing || !isStepValid}
              onClick={() => {
                if (!isLastStep) {
                  handleNext();
                } else {
                  setShowMobileConfirmModal(true);
                }
              }}
              className="px-6 py-2 bg-black text-white rounded-full bg-redlight min-w-[100px]"
            >
              {isCurrentProcessing ? (
                <ProcessingBar />
              ) : (
                <>
                  <span className="hidden sm:inline">
                    {isLastStep
                      ? formData.paymentMethod === "online"
                        ? "Pay with Stripe"
                        : "Place Order"
                      : "Next"}
                  </span>
                  <span className="inline sm:hidden">
                    {isLastStep ? "Confirm" : <FaAngleRight size={20} />}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <MobileConfirmModal
        isOpen={showMobileConfirmModal}
        onClose={() => !isProcessing && setShowMobileConfirmModal(false)}
        cart={cartWithOperator}
        formData={formData}
        totalAmount={totalAmount}
        isProcessing={isProcessing}
        onPlaceOrder={handleConfirm}
      />
    </>
  );
};

export default RenderSidebar;
